import { ITeamMember, ITeamUserGroup } from "@/interfaces/responses/team";
import { UserRole } from "@/interfaces/store/IUserState";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import { isFrenchCompliant } from "@/utils/vue-helpers/compliance";

/**
 * The group the Team section manages: the group of the POS config this till
 * operates. Fails CLOSED — no selected config means no team group and no
 * section. Deliberately not UserModule.getUserGroupId or
 * POSConfigModule.groupId: both prefer the user's FIRST confirmed group,
 * which for multi-group accounts can differ from the till's group and would
 * gate (and mutate!) the wrong account.
 */
export function getTeamGroupId(): number | null {
  return POSConfigModule.selectedConfig?.group || null;
}

/**
 * Whether the signed-in user is a confirmed Owner of the till's group.
 * Gates the owner-only Team section.
 */
export function isTeamGroupOwner(): boolean {
  const groupId = getTeamGroupId();

  if (!groupId) {
    return false;
  }

  return UserModule.getUserGroups.some(
    (userGroup) =>
      userGroup.group.id === groupId &&
      userGroup.role === UserRole.ROLE_OWNER &&
      userGroup.state === 1
  );
}

/**
 * Whether the Team section may be used on this till.
 *
 * Two conditions, both required: the signed-in user is a confirmed Owner of
 * the till's group, and that group runs under French fiscal compliance.
 * Managing operators from the till is part of the NF525 scope, so every other
 * country keeps doing it in the back office.
 */
export function canManageTeamOnPos(): boolean {
  return isFrenchCompliant.value && isTeamGroupOwner();
}

/**
 * Map a raw GET /group/{groupId}/role entry to the flat row model
 * consumed by the Team table (same mapping as the back-office team store).
 */
export function mapTeamMember(userGroup: ITeamUserGroup): ITeamMember {
  const firstName = userGroup.user?.firstName || "";
  const lastName = userGroup.user?.lastName || "";

  return {
    roleId: userGroup.id,
    userId: userGroup.user?.id ?? null,
    fullName: `${firstName} ${lastName}`.trim(),
    email:
      userGroup.validationEmail ||
      userGroup.user?.emails?.[0]?.emailAddress ||
      "",
    roleType: userGroup.role,
    roleName: userGroup.roleName,
    state: userGroup.state,
    socialSecurityNumber: userGroup.user?.socialSecurityNumber || "",
  };
}
